import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChartLineUp, Lightning, Plus, X, PaperPlaneTilt, Check, Trash, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const PRODUCT_META = {
  stocks: { name: "Stocks", accent: "#F59E0B", icon: ChartLineUp, endpoint: "/admin/stocks/picks", feedKey: "stocks" },
  options: { name: "Options", accent: "#A855F7", icon: Lightning, endpoint: "/admin/options/picks", feedKey: "options" },
};

const initialStock = { ticker: "", direction: "long", entry_price: "", target_1: "", target_2: "", stop_loss: "", rationale: "" };
const initialOption = { underlying: "", option_type: "call", strike: "", expiration: "", entry_price: "", target_1: "", target_2: "", stop_loss: "", contracts: 1, rationale: "" };

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <input className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white/40 rounded-sm px-3 py-2 text-sm font-mono-d outline-none" {...props} />
    </label>
  );
}

export function NewPickForm({ product, onCreated }) {
  const meta = PRODUCT_META[product];
  const [form, setForm] = useState(product === "stocks" ? initialStock : initialOption);
  const [saving, setSaving] = useState(false);
  const [sendImpromptu, setSendImpromptu] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["entry_price", "target_1", "target_2", "stop_loss", "strike", "contracts"].forEach((k) => {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
        if (payload[k] === "") delete payload[k];
      });
      const { data } = await api.post(meta.endpoint, payload);
      toast.success(`${product === "stocks" ? data.ticker : data.underlying} pick created`);
      if (sendImpromptu) {
        try {
          const res = await api.post("/admin/picks/impromptu-send", {
            pick_id: data.pick_id, product, audience: "active",
          });
          toast.success(`Impromptu email sent · ${res.data.sent} recipient(s)`);
        } catch (err) {
          toast.error(`Pick saved but impromptu email failed: ${err.response?.data?.detail || err.message}`);
        }
      }
      setForm(product === "stocks" ? initialStock : initialOption);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#121212] border rounded-sm p-5 space-y-4" style={{ borderColor: `${meta.accent}44` }} data-testid={`new-${product}-form`}>
      <div className="flex items-baseline gap-2">
        <Plus size={14} style={{ color: meta.accent }} />
        <h3 className="font-display text-2xl">New {meta.name.slice(0, -1)} pick</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {product === "stocks" ? (
          <>
            <Field label="Ticker" data-testid={`form-${product}-ticker`} value={form.ticker} onChange={(e) => update("ticker", e.target.value.toUpperCase())} placeholder="NVDA" required maxLength={12} />
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Direction</span>
              <select className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-sm font-mono-d" value={form.direction} onChange={(e) => update("direction", e.target.value)} data-testid={`form-${product}-direction`}>
                <option value="long">Long</option>
                <option value="short">Short</option>
              </select>
            </label>
          </>
        ) : (
          <>
            <Field label="Underlying" data-testid={`form-${product}-underlying`} value={form.underlying} onChange={(e) => update("underlying", e.target.value.toUpperCase())} placeholder="SPY" required maxLength={12} />
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Type</span>
              <select className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-sm font-mono-d" value={form.option_type} onChange={(e) => update("option_type", e.target.value)} data-testid={`form-${product}-type`}>
                <option value="call">Call</option>
                <option value="put">Put</option>
              </select>
            </label>
            <Field label="Strike" data-testid={`form-${product}-strike`} value={form.strike} onChange={(e) => update("strike", e.target.value)} type="number" step="0.01" required />
            <Field label="Expiration" data-testid={`form-${product}-expiration`} value={form.expiration} onChange={(e) => update("expiration", e.target.value)} type="date" required />
          </>
        )}

        <Field label={product === "stocks" ? "Entry $" : "Entry premium $"} data-testid={`form-${product}-entry`} value={form.entry_price} onChange={(e) => update("entry_price", e.target.value)} type="number" step="0.01" required />
        <Field label="Target 1 $" data-testid={`form-${product}-t1`} value={form.target_1} onChange={(e) => update("target_1", e.target.value)} type="number" step="0.01" required />
        <Field label="Target 2 $" data-testid={`form-${product}-t2`} value={form.target_2} onChange={(e) => update("target_2", e.target.value)} type="number" step="0.01" />
        <Field label="Stop Loss $" data-testid={`form-${product}-stop`} value={form.stop_loss} onChange={(e) => update("stop_loss", e.target.value)} type="number" step="0.01" required />
        {product === "options" && (
          <Field label="Contracts" data-testid={`form-${product}-contracts`} value={form.contracts ?? ""} onChange={(e) => update("contracts", e.target.value)} type="number" min={1} />
        )}
      </div>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Rationale (free text — emailed to subscribers)</span>
        <textarea
          className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white/40 rounded-sm px-3 py-2 text-sm leading-relaxed outline-none"
          rows={3}
          value={form.rationale}
          onChange={(e) => update("rationale", e.target.value)}
          maxLength={4000}
          data-testid={`form-${product}-rationale`}
          placeholder="Why this trade? Catalyst, technical setup, key levels…"
        />
      </label>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input type="checkbox" checked={sendImpromptu} onChange={(e) => setSendImpromptu(e.target.checked)} data-testid={`form-${product}-impromptu`} />
          <PaperPlaneTilt size={14} /> Send immediate impromptu email
        </label>
        <button type="submit" disabled={saving} data-testid={`form-${product}-submit`} className="px-5 py-2 font-bold text-xs uppercase tracking-widest rounded-sm disabled:opacity-40 text-white" style={{ background: meta.accent }}>
          {saving ? "Saving…" : "Save pick" + (sendImpromptu ? " + send" : "")}
        </button>
      </div>
    </form>
  );
}

function CloseModal({ pick, product, onClose, onClosed }) {
  const [outcome, setOutcome] = useState("hit_t1");
  const [exitPrice, setExitPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = PRODUCT_META[product];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/${product}/picks/${pick.pick_id}/close`, {
        outcome, exit_price: Number(exitPrice), notes: notes || null,
      });
      toast.success(`${product === "stocks" ? pick.ticker : pick.underlying} closed: ${outcome}`);
      onClosed();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0F0F0F] border rounded-sm p-6 max-w-md w-full" style={{ borderColor: meta.accent }} data-testid={`close-modal-${pick.pick_id}`}>
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-2xl">Close {product === "stocks" ? pick.ticker : `${pick.underlying} ${pick.strike} ${(pick.option_type || "").toUpperCase()}`}</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Outcome</span>
          <select className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-sm font-mono-d" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="close-outcome">
            <option value="hit_t1">Hit Target 1</option>
            <option value="hit_t2">Hit Target 2</option>
            <option value="stopped">Stopped out</option>
            <option value="closed_manual">Closed (manual exit)</option>
            {product === "options" && <option value="expired">Expired worthless</option>}
          </select>
        </label>
        <Field label={product === "stocks" ? "Final exit price $" : "Final exit premium $"} data-testid="close-exit" type="number" step="0.01" value={exitPrice} onChange={(e) => setExitPrice(e.target.value)} required />
        <label className="block mt-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Notes (optional)</span>
          <textarea className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-sm" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="close-notes" />
        </label>
        <button type="submit" disabled={busy} data-testid="close-submit" className="w-full mt-4 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm disabled:opacity-40 text-white" style={{ background: meta.accent }}>
          {busy ? "Closing…" : "Mark closed"}
        </button>
      </form>
    </div>
  );
}

function OpenPickRow({ pick, product, onClose, onDelete, onBlast, blastingId }) {
  const isStock = product === "stocks";
  const alreadySent = pick.delivery_status === "sent";
  const isBlasting = blastingId === pick.pick_id;
  return (
    <tr className="border-b border-white/5" data-testid={`open-row-${pick.pick_id}`}>
      <td className="px-3 py-2 font-mono-d text-xs text-zinc-500">{new Date(pick.posted_at).toLocaleDateString()}</td>
      <td className="px-3 py-2 font-bold">{isStock ? pick.ticker : pick.underlying}</td>
      {!isStock && <td className="px-3 py-2 text-xs font-mono-d text-zinc-400">${pick.strike} {(pick.option_type || "").toUpperCase()} {pick.expiration}</td>}
      <td className="px-3 py-2 font-mono-d text-xs">${pick.entry_price?.toFixed(2)}</td>
      <td className="px-3 py-2 font-mono-d text-xs text-[#22C55E]">${pick.target_1?.toFixed(2)}{pick.target_2 ? ` / $${pick.target_2.toFixed(2)}` : ""}</td>
      <td className="px-3 py-2 font-mono-d text-xs text-[#FF3B30]">${pick.stop_loss?.toFixed(2)}</td>
      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
        <button
          onClick={() => onBlast(pick)}
          disabled={isBlasting}
          title={alreadySent ? "Already emailed once — click to send again" : "Send impromptu email to subscribers now"}
          data-testid={`blast-btn-${pick.pick_id}`}
          className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold rounded-sm transition-colors disabled:opacity-40 ${
            alreadySent
              ? "border border-zinc-600 text-zinc-400 hover:border-zinc-400 hover:text-white"
              : "border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/15"
          }`}
        >
          <PaperPlaneTilt size={11} weight="fill" />
          {isBlasting ? "Sending…" : alreadySent ? "Resend" : "Blast"}
        </button>
        <button onClick={() => onClose(pick)} data-testid={`close-btn-${pick.pick_id}`} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold rounded-sm bg-white text-black hover:bg-white/90">
          Close
        </button>
        <button onClick={() => onDelete(pick.pick_id)} data-testid={`delete-btn-${pick.pick_id}`} className="px-1.5 py-1 text-zinc-500 hover:text-[#FF3B30]">
          <Trash size={12} />
        </button>
      </td>
    </tr>
  );
}

function ProductPanel({ product }) {
  const meta = PRODUCT_META[product];
  const [picks, setPicks] = useState([]);
  const [closeTarget, setCloseTarget] = useState(null);
  const [blastingId, setBlastingId] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/${product}/picks?status=OPEN`);
    setPicks(data.picks);
  };
  useEffect(() => { load(); }, [product]);

  const onDelete = async (pickId) => {
    if (!window.confirm("Delete this pick? This is permanent and won't appear in history.")) return;
    try {
      await api.delete(`/admin/${product}/picks/${pickId}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onBlast = async (pick) => {
    const label = product === "stocks" ? pick.ticker : `${pick.underlying} ${pick.strike} ${(pick.option_type || "").toUpperCase()}`;
    const already = pick.delivery_status === "sent";
    const msg = already
      ? `Resend impromptu email for ${label}? Subscribers will receive a second alert.`
      : `Send impromptu email for ${label} to ${product === "stocks" ? "Stocks" : "Options"} subscribers now?`;
    if (!window.confirm(msg)) return;
    setBlastingId(pick.pick_id);
    try {
      const { data } = await api.post("/admin/picks/impromptu-send", {
        pick_id: pick.pick_id, product, audience: "active",
      });
      toast.success(`Sent to ${data.sent || 0} subscriber(s)${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blast failed");
    } finally {
      setBlastingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <NewPickForm product={product} onCreated={load} />
      <div className="bg-[#121212] border border-white/10 rounded-sm overflow-x-auto" data-testid={`${product}-open-table`}>
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-display text-xl">Open positions <span className="text-zinc-500 text-sm ml-2">{picks.length}</span></h3>
        </div>
        {picks.length === 0 ? (
          <div className="text-xs text-zinc-500 italic px-4 py-6 text-center">No open positions.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left px-3 py-2">Posted</th>
                <th className="text-left px-3 py-2">{product === "stocks" ? "Ticker" : "Underlying"}</th>
                {product === "options" && <th className="text-left px-3 py-2">Contract</th>}
                <th className="text-left px-3 py-2">Entry</th>
                <th className="text-left px-3 py-2">T1 / T2</th>
                <th className="text-left px-3 py-2">Stop</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {picks.map((p) => <OpenPickRow key={p.pick_id} pick={p} product={product} onClose={setCloseTarget} onDelete={onDelete} onBlast={onBlast} blastingId={blastingId} />)}
            </tbody>
          </table>
        )}
      </div>
      {closeTarget && <CloseModal pick={closeTarget} product={product} onClose={() => setCloseTarget(null)} onClosed={load} />}
    </div>
  );
}

export default function MarketsAdmin() {
  const [tab, setTab] = useState("stocks");
  const meta = PRODUCT_META[tab];
  const Icon = meta.icon;

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-10">
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E] mb-2">Admin · Editorial picks</div>
        <h1 className="font-display text-5xl leading-none">MARKETS</h1>
        <p className="text-sm text-zinc-400 max-w-3xl mt-3 leading-relaxed">
          Manually post Stock and Options picks. Each pick is timestamped, locked at posting time, and (when impromptu is checked) emailed immediately to subscribers of the matching pack.
        </p>
      </motion.div>

      <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden mb-6 shadow-lg" data-testid="markets-tabs">
        {["stocks", "options"].map((t) => {
          const accent = PRODUCT_META[t].accent;
          const tintTextColor = t === "stocks" ? "#FCD34D" : "#D8B4FE";
          const active = tab === t;
          return (
            <button key={t} onClick={() => setTab(t)} data-testid={`markets-tab-${t}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all ${
                active ? "text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]" : "hover:text-white"
              } ${t !== "stocks" ? "border-l-2 border-white/20" : ""}`}
              style={active
                ? { background: accent }
                : { background: `${accent}26`, color: tintTextColor }}>
              {React.createElement(PRODUCT_META[t].icon, { size: 15, weight: "fill" })}
              {PRODUCT_META[t].name}
            </button>
          );
        })}
      </div>

      <ProductPanel product={tab} />
    </div>
  );
}
